import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import buttons from 'src/lib/ui/config/buttons';
import { animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import { getScreenDimensions, waitForItPromise } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { CardsRibbon } from '../components/CardsRibbon';
import { CardsShine } from '../components/CardsShine';
import gsap from 'gsap';
import { Card, CardState } from '../components/Card';

type CloseHandler = () => void;
type CardTarget = Pick<ViewOpts, 'x' | 'y' | 'r' | 'scale'>;

const CARD_WIDTH = 184;
const CARD_HEIGHT = 238;
const REVEAL_SCALE = 3;
const REVEAL_BACK_HOLD_DURATION = 150;
const RIBBON_Y = 210;
const GRID_TOP = 260;
const GRID_BOTTOM_MARGIN = 20;
const CARD_HORIZONTAL_GAP = 50;
const CARD_ROW_GAP = 80;
const CARD_ROWS_BY_COUNT = {
  1: [1],
  2: [2],
  3: [2, 1],
  4: [2, 2],
  5: [3, 2],
  6: [3, 3],
} as const;
const CARD_SCALE_BY_COUNT = {
  1: 2,
  2: 1.3,
  3: 1.2,
  4: 1.1,
  5: 1,
  6: 1,
} as const;

export default class PopupCardsReceived extends PopupBasicLazy {
  private claimButton: ButtonScaleViewWithText;
  private shine: CardsShine;
  private ribbon: CardsRibbon;
  private cards: Card[] = [];
  private cardTargets: CardTarget[] = [];
  private cardsContainer: View;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.POPUP_RECEIVED,
      },
      zIndex: 10000,
    });

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    this.close = opts.close;

    this.ribbon = new CardsRibbon({
      superview: this.box,
      x: this.box.style.width / 2,
      y: RIBBON_Y,
      centerOnOrigin: true,
      scale: 0,
    });

    this.buttonClose.updateOpts({
      visible: false,
    });

    this.shine = new CardsShine({
      superview: this.box,
      x: width / 2,
      y: height / 2,
      radius: 350,
      opacity: 0.7,
      scale: 0,
    });

    this.cardsContainer = new View({
      superview: this.box,
      x: 0,
      y: 0,
      width: this.box.style.width,
      height,
    });

    this.claimButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: 317,
      height: 95,
      fontSize: 40,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: this.box.style.height - 200,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.POPUP_RECEIVED_COLLECT,
      },
      localeText: () => i18n('cards.cardsReceivedPopup.collect'),
      onClick: async () => this.claim(),
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(AssetGroup.constructURLs(['assets/ui/cards/common']));
  }

  public async init(args: { cards: CardState[] }) {
    super.init({});

    this.ribbon.setText(
      i18n('cards.cardsReceivedPopup.youReceived', { num: args.cards.length }),
    );

    this.claimButton.hide();

    this.cardTargets = this.getCardTargets(args.cards.length);

    this.cards = args.cards.map((cardState, index) => {
      const card = new Card({
        superview: this.cardsContainer,
        centerOnOrigin: true,
      });
      card.setState(cardState);
      card.flip(true, 0);
      card.updateOpts(this.cardTargets[index]);
      return card;
    });

    this.cardsContainer.updateOpts({
      opacity: 0,
    });
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();
    this.bg.updateOpts({ opacity: 0 });
    await this.loadAssets();
    this.overlay.hide();
    this.box.show();
    await gsap.to(this.bg.style, {
      opacity: 1,
      duration: 0.2,
      ease: 'linear',
    });
    this.playAnimation();
  }

  public async fadeOut() {
    this.overlay.show();
    this.shine.stopAutoUpdate();
    animate(this.box)
      .clear()
      .wait(0)
      .then({ opacity: 0 }, 200, animate.easeOut)
      .toPromise()
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });
  }

  private async playAnimation() {
    sounds.sfx.play('tada');

    this.ribbon.updateOpts({
      visible: true,
      scale: 0,
    });

    await gsap.to(this.ribbon.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    sounds.sfx.play('gachaItemDrop');

    this.shine.autoUpdate();
    await gsap.to(this.shine.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    await this.revealCards();

    this.claimButton.updateOpts({
      scale: 0,
      visible: true,
    });

    gsap.to(this.claimButton.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }

  private getCardRows(cardCount: number) {
    if (cardCount <= 0) return [];

    const rows =
      CARD_ROWS_BY_COUNT[cardCount as keyof typeof CARD_ROWS_BY_COUNT];
    if (rows) return rows;

    return new Array(Math.ceil(cardCount / 3))
      .fill(0)
      .map((_, index) => Math.min(3, cardCount - index * 3));
  }

  private getGridScale(cardCount: number) {
    return (
      CARD_SCALE_BY_COUNT[cardCount as keyof typeof CARD_SCALE_BY_COUNT] ?? 1
    );
  }

  private getGridBottom() {
    return (
      this.claimButton.style.y -
      this.claimButton.style.height / 2 -
      GRID_BOTTOM_MARGIN
    );
  }

  private getCardTargets(cardCount: number): CardTarget[] {
    const rows = this.getCardRows(cardCount);
    if (rows.length === 0) return [];

    const scale = this.getGridScale(cardCount);
    const centerX = this.box.style.width / 2;
    const centerY = (GRID_TOP + this.getGridBottom()) / 2;
    const scaledWidth = CARD_WIDTH * scale;
    const scaledHeight = CARD_HEIGHT * scale;
    const totalHeight =
      rows.length * scaledHeight + (rows.length - 1) * CARD_ROW_GAP;
    const targets: CardTarget[] = [];

    rows.forEach((cardsInRow, rowIndex) => {
      const totalWidth =
        cardsInRow * scaledWidth +
        Math.max(0, cardsInRow - 1) * CARD_HORIZONTAL_GAP;
      const y =
        centerY -
        totalHeight / 2 +
        scaledHeight / 2 +
        rowIndex * (scaledHeight + CARD_ROW_GAP);

      for (let columnIndex = 0; columnIndex < cardsInRow; columnIndex++) {
        targets.push({
          x:
            centerX -
            totalWidth / 2 +
            scaledWidth / 2 +
            columnIndex * (scaledWidth + CARD_HORIZONTAL_GAP),
          y,
          r: 0,
          scale,
        });
      }
    });

    return targets;
  }

  private async revealCards() {
    gsap.to(this.cardsContainer.style, {
      opacity: 1,
      duration: 0.2,
      ease: 'quad.out',
    });

    const baseX = this.box.style.width / 2;
    const baseY = (GRID_TOP + this.getGridBottom()) / 2;
    const spacing = this.cards.length > 0 ? 30 / this.cards.length : 0;

    this.cards.forEach((card, i) => {
      card.updateOpts({
        x: baseX + spacing * i,
        y: baseY + spacing * i,
        r: 0.05,
        scale: 0,
        zIndex: -i, // The last one should be in the back
      });
    });

    for (const [i, card] of this.cards.entries()) {
      card.updateOpts({ zIndex: i });
      await this.revealCard(card, this.cardTargets[i]);
    }
  }

  private async revealCard(card: Card, target?: CardTarget) {
    const x = target?.x ?? 0;
    const y = target?.y ?? 0;
    const r = target?.r ?? 0;
    const scale = target?.scale ?? 1;

    await gsap.to(card.style, {
      scale: REVEAL_SCALE,
      duration: 0.3,
      ease: 'back.out',
    });
    await waitForItPromise(REVEAL_BACK_HOLD_DURATION);
    await card.flip(false, 300);
    await gsap.to(card.style, {
      x,
      y,
      r,
      scale,
      duration: 0.3,
      ease: 'quad.inOut',
    });
  }

  private async claim() {
    sounds.sfx.play('win');
    this.claimButton.hide();
    this.close();
  }
}
